import React, { Fragment } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { getUserRoles, isLogged } from './store/auth/selectors';
import { connect } from 'react-redux';
import { AppState } from './store/rootReducer';
import routes from './resources/routes';

import { HomePage } from './containers/HomePage';
import { LoginPage } from './containers/LoginPage';
import { NotFoundPage } from './containers/NotFoundPage';
import { ResetPasswordPage } from './containers/ResetPasswordPage';
import NewsAndEvents from './containers/NewsAndEvents/NewsAndEvents';
import KnowledgePage from './containers/KnowledgePage/KnowledgePage';
import { UsersPage } from './containers/UsersPage';
import { AppUsersPage } from './containers/AppUsersPage';
import { MissionsPage } from './containers/MissionsPage';
import UserRoles from './resources/enums/UserRoles';
import RewardsPage from './containers/RewardsPage/RewardsPage';
import AdditionalStagesPage from './containers/AdditionalStagesPage/AdditionalStagesPage';
import { DocumentsPage } from './containers/DocumentsPage';
import { RegulationsPage } from './containers/RegulationsPage';
import { MeetingsPage } from './containers/MeetingsPage';
import { ProductsPage } from './containers/ProductsPage';
import AboutMePage from './containers/AboutMePage/AboutMePage';
import ChatPage from './containers/ChatPage/ChatPage';
import ChatSchedulePage from './containers/ChatSchedulePage/ChatSchedulePage';

const AppRoutes = (props: { isLogged: boolean; roles: string[] }) => {
    return (
        <BrowserRouter>
            <Fragment>
                <Routes>
                    <Route path={routes.LOGIN} element={publicRoute(props.isLogged, LoginPage)} />
                    <Route path={routes.HOME} element={protectedRoute(props.isLogged, HomePage)} />
                    <Route
                        path={routes.USERS}
                        element={protectedRoute(
                            props.isLogged,
                            UsersPage,
                            checkIfAllowed(props.roles, [UserRoles.SuperAdmin]),
                        )}
                    />
                    <Route
                        path={routes.CHARGEABLE_PRODUCTS}
                        element={protectedRoute(
                            props.isLogged,
                            ProductsPage,
                            checkIfAllowed(props.roles, [UserRoles.Admin, UserRoles.SuperAdmin]),
                        )}
                    />
                    <Route
                        path={routes.KNOWLEDGE}
                        element={protectedRoute(
                            props.isLogged,
                            KnowledgePage,
                            checkIfAllowed(props.roles, [UserRoles.Admin, UserRoles.SuperAdmin]),
                        )}
                    />
                    <Route
                        path={routes.NEWS_AND_EVENTS}
                        element={protectedRoute(
                            props.isLogged,
                            NewsAndEvents,
                            checkIfAllowed(props.roles, [UserRoles.Admin, UserRoles.SuperAdmin]),
                        )}
                    />
                    <Route
                        path={routes.ADDITIONAL_MISSIONS}
                        element={protectedRoute(
                            props.isLogged,
                            AdditionalStagesPage,
                            checkIfAllowed(props.roles, [UserRoles.Admin, UserRoles.SuperAdmin]),
                        )}
                    />
                    <Route
                        path={routes.APP_USERS}
                        element={protectedRoute(
                            props.isLogged,
                            AppUsersPage,
                            checkIfAllowed(props.roles, [UserRoles.Admin, UserRoles.SuperAdmin]),
                        )}
                    />
                    <Route
                        path={routes.MISSIONS}
                        element={protectedRoute(
                            props.isLogged,
                            MissionsPage,
                            checkIfAllowed(props.roles, [UserRoles.Admin, UserRoles.SuperAdmin]),
                        )}
                    />
                    <Route
                        path={routes.REWARDS}
                        element={protectedRoute(
                            props.isLogged,
                            RewardsPage,
                            checkIfAllowed(props.roles, [UserRoles.Admin, UserRoles.SuperAdmin]),
                        )}
                    />
                    <Route
                        path={routes.DOCUMENTS}
                        element={protectedRoute(
                            props.isLogged,
                            DocumentsPage,
                            checkIfAllowed(props.roles, [UserRoles.Admin, UserRoles.SuperAdmin]),
                        )}
                    />
                    <Route
                        path={routes.ABOUT}
                        element={protectedRoute(
                            props.isLogged,
                            AboutMePage,
                            checkIfAllowed(props.roles, [UserRoles.Coach]),
                        )}
                    />
                    <Route
                        path={routes.MEETINGS}
                        element={protectedRoute(
                            props.isLogged,
                            MeetingsPage,
                            checkIfAllowed(props.roles, [UserRoles.Coach]),
                        )}
                    />
                    <Route
                        path={routes.CHAT}
                        element={protectedRoute(
                            props.isLogged,
                            ChatPage,
                            checkIfAllowed(props.roles, [UserRoles.Coach]),
                        )}
                    />
                    <Route
                        path={routes.CHAT_SCHEDULE}
                        element={protectedRoute(
                            props.isLogged,
                            ChatSchedulePage,
                            checkIfAllowed(props.roles, [UserRoles.SuperAdmin]),
                        )}
                    />
                    <Route
                        path={`${routes.REGULATIONS}/*`}
                        element={<RegulationsPage language={getLanguage(window.location.href)} />}
                    />
                    <Route path={routes.RESET} element={<ResetPasswordPage />} />
                    <Route path={routes.NOT_FOUND} element={<NotFoundPage />} />
                </Routes>
            </Fragment>
        </BrowserRouter>
    );
};

function protectedRoute(isLogged: boolean, Component: React.FC, allowed?: boolean) {
    return !isLogged ? (
        <Navigate to={routes.LOGIN} />
    ) : allowed === undefined || allowed ? (
        <Component />
    ) : (
        <Navigate to={routes.HOME} />
    );
}

function publicRoute(isLogged: boolean, Component: React.FC) {
    return isLogged ? <Navigate to={routes.HOME} /> : <Component />;
}

function mapStateToProps(state: AppState) {
    return { isLogged: isLogged(state), roles: getUserRoles(state) };
}

function checkIfAllowed(userRoles: string[], requiredRoles?: string[]) {
    return !requiredRoles || userRoles.some((r) => requiredRoles?.includes(r));
}

function getLanguage(path: string) {
    const splited = path.split('/');
    return splited.length > 0 ? splited[splited.length - 1] : 'pl';
}

export default connect(mapStateToProps)(AppRoutes);
